/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 📄 File: CdrFilterFormView.tsx
 🧩 Description:
    Form view for creating and editing CDR Filters.
    Logic → useCdrFilterForm  |  Sections → CdrFilter*Box components
────────────────────────────────────────────────────────────────*/

import { Filter } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';
import { ModuleFormHeader } from '../../components/layouts';
import FixedActionBar from '../../components/ui/FixedActionBar';
import { CdrFilterGeneralBox } from './components/CdrFilterGeneralBox';
import { CdrFilterConditionsBox } from './components/CdrFilterConditionsBox';
import { useCdrFilterForm } from './hooks/useCdrFilterForm';

export default function CdrFilterFormView() {
    const { t } = useI18n();

    const {
        isEditMode, duplicateId, description, duration, talkTime, searchConditions,
        isLoading, isSaving, errors,
        setDescription, setDuration, setTalkTime,
        handleAddCondition, handleRemoveCondition, handleConditionChange,
        handleSaveWithAction, handleCancel,
    } = useCdrFilterForm();

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-muted-foreground">{t('common.loading')}</div>
            </div>
        );
    }

    return (
        <div className="p-6 pb-12 space-y-4">
            <ModuleFormHeader
                icon={<Filter className="h-5 w-5 text-primary" />}
                title={
                    isEditMode
                        ? `${t('cdrFilters.title')} - ${description || ''}`
                        : t('cdrFilters.actions.add')
                }
                backButtonText={t('common.list')}
                onBack={handleCancel}
            />

            <div className="mt-4 space-y-4">
                <CdrFilterGeneralBox
                    description={description}
                    duration={duration}
                    talkTime={talkTime}
                    errors={errors}
                    onDescriptionChange={setDescription}
                    onDurationChange={(patch) => setDuration(prev => ({ ...prev, ...patch }))}
                    onTalkTimeChange={(patch) => setTalkTime(prev => ({ ...prev, ...patch }))}
                />

                <CdrFilterConditionsBox
                    searchConditions={searchConditions}
                    errors={errors}
                    onAdd={handleAddCondition}
                    onRemove={handleRemoveCondition}
                    onChange={handleConditionChange}
                />
            </div>

            <FixedActionBar
                onSaveWithAction={handleSaveWithAction}
                onCancel={handleCancel}
                isSaving={isSaving}
                isEditMode={isEditMode}
                useSaveDropdown={true}
                saveText={t('common.save')}
                cancelText={t('common.cancel')}
            />
        </div>
    );
}
